/**
 * useSubscriptionWithLoading Hook
 * Provides subscription data with safe accessors and loading states
 *
 * This hook encapsulates the loading pattern for subscription data,
 * preventing the anti-pattern of using optional chaining everywhere
 * and providing safe defaults during loading states.
 *
 * @example
 * ```tsx
 * const { tier, safeUsage, loading, hasData } = useSubscriptionWithLoading()
 *
 * // tier is always defined (defaults to 'free' during load)
 * // safeUsage never returns null
 * // Use 'loading' to show skeleton UI
 * // Use 'hasData' to check if real data is available
 * ```
 */

import { useSubscription } from '../contexts/SubscriptionContext'
import { SubscriptionDetails, UsageStats, SubscriptionTier } from '../types/subscription'

export interface UseSubscriptionWithLoadingResult {
  // Raw data (can be null during loading)
  subscription: SubscriptionDetails | null
  usage: UsageStats | null
  error: Error | null

  // Loading state flags
  loading: boolean
  hasData: boolean

  // Computed helpers with safe defaults
  tier: SubscriptionTier
  isPaidTier: boolean
  isInTrial: boolean

  // Safe accessors (never null/undefined)
  safeUsage: {
    weeklyRerolls: { used: number; limit: number | null; weekStart: string | null }
    mealPlansThisMonth: number
    globalChatThisWeek: number
  }
  safeLimits: {
    mealPlansPerMonth: number | null
    globalChatPerWeek: number | null
    weeklyRerolls: number | null
  }

  // Actions
  refreshSubscription: () => Promise<void>
  openPlatformPortal: () => Promise<void>
  /** Increment the global weekly reroll counter in local state immediately (no network round-trip) */
  optimisticIncrementRerolls: () => void
}

export function useSubscriptionWithLoading(): UseSubscriptionWithLoadingResult {
  const {
    subscription,
    usage,
    loading,
    error,
    refreshSubscription,
    optimisticIncrementRerolls,
    openPlatformPortal
  } = useSubscription()

  // Computed helpers
  const tier = subscription?.tier || 'free'
  const isPaidTier = tier === 'premium'
  const isInTrial = subscription?.isInTrial || false
  const hasData = !loading && subscription !== null && usage !== null

  // Safe accessors that never return null
  const safeUsage = {
    weeklyRerolls: usage?.usage?.weeklyRerolls ?? { used: 0, limit: null, weekStart: null },
    mealPlansThisMonth: usage?.usage?.mealPlansThisMonth || 0,
    globalChatThisWeek: usage?.usage?.globalChatThisWeek || 0
  }

  const safeLimits = {
    mealPlansPerMonth: usage?.limits?.mealPlansPerMonth ?? null,
    globalChatPerWeek: usage?.limits?.globalChatPerWeek ?? null,
    weeklyRerolls: usage?.limits?.weeklyRerolls ?? null
  }

  return {
    subscription,
    usage,
    loading,
    error,
    hasData,
    tier,
    isPaidTier,
    isInTrial,
    safeUsage,
    safeLimits,
    refreshSubscription,
    optimisticIncrementRerolls,
    openPlatformPortal
  }
}
